import type {
  CustomSeriesRenderItem,
  CustomSeriesRenderItemAPI,
  CustomSeriesRenderItemReturn,
  EChartsOption,
  TooltipComponentFormatterCallbackParams,
} from "echarts";
import type { EChartsThemeTokens } from "@/shared/charts/theme/create-echarts-theme";
import type {
  GeoDirectionalLine,
  GeoHeatmapLayer,
  GeoLineSegment,
  GeoScatterBoundary,
  GeoScatterBoundaryStyle,
  GeoScatterChartConfig,
  GeoScatterMarker,
  GeoScatterPoint,
} from "@/shared/charts/components/geo-scatter-chart/types";

/** Solid black outline over a transparent blue fill, matching the default look for highlighted boundary regions. */
export const DEFAULT_BOUNDARY_STYLE: Required<GeoScatterBoundaryStyle> = {
  fill: "rgba(33, 150, 243, 0.15)",
  stroke: "#000000",
  strokeWidth: 2.5,
  lineDash: "solid",
  animated: false,
  pulse: false,
};

function toMarkerDatum(marker: GeoScatterMarker) {
  return {
    id: marker.id,
    name: marker.name ?? marker.id,
    selected: marker.selected,
    value: marker.position,
    tooltip: marker.tooltip,
    itemStyle: {
      color: marker.color,
      borderColor: marker.borderColor,
      borderWidth: marker.borderWidth,
      shadowBlur: marker.shadowBlur,
      shadowColor: marker.shadowColor,
    },
  };
}

function createHeatmapRenderItem(layer: GeoHeatmapLayer): CustomSeriesRenderItem {
  return (_params, api) => {
    const topLeft = api.coord([layer.x, layer.y]);
    const bottomRight = api.coord([layer.x + layer.width, layer.y + layer.height]);

    return {
      type: "image",
      silent: true,
      style: {
        image: layer.image,
        x: topLeft[0],
        y: topLeft[1],
        width: bottomRight[0] - topLeft[0],
        height: bottomRight[1] - topLeft[1],
      },
    };
  };
}

function formatPointLabel(point: GeoScatterPoint): string {
  const label = point.label;
  if (!label) return "";
  if (!label.secondaryText) return `{primary|${label.primaryText}}`;

  return `{primary|${label.primaryText}}\n{secondary|${label.secondaryText}}`;
}

/**
 * Base point label font size in pixels at labelScale 1. Multiplied by a
 * labelScale factor (see computeLabelScale below) so labels stay legible at
 * every zoom level.
 */
const BASE_POINT_LABEL_FONT_SIZE = 13;
const MAX_LABEL_SCALE = 1.15;
const MIN_LABEL_SCALE = 1.1;
/**
 * Absolute floor for the point label font size, in pixels, regardless of
 * labelScale. Guards against labels becoming unreadably tiny if the map is
 * zoomed in a lot, even beyond what MIN_LABEL_SCALE alone would allow for.
 */
const MIN_POINT_LABEL_FONT_SIZE = 11;

function computeLabelScale(zoom: number, zoomLimit: { min: number; max: number }): number {
  // Intentionally inverse to the map's own zoom (unlike a point's physical
  // footprint, which should get visually bigger the further in you zoom):
  // labels are largest at the layer's minimum zoom (fully zoomed out, where
  // the whole schematic - and its labels - are smallest on screen) and ease
  // down to their smallest at maximum zoom, so they stay readable at every
  // zoom level instead of shrinking exactly when the map is hardest to read.
  if (zoomLimit.max <= zoomLimit.min) return 1;

  const zoomedInRatio = (zoom - zoomLimit.min) / (zoomLimit.max - zoomLimit.min);
  const clampedRatio = Math.min(1, Math.max(0, zoomedInRatio));

  return MAX_LABEL_SCALE + clampedRatio * (MIN_LABEL_SCALE - MAX_LABEL_SCALE);
}

function toPointDatum(point: GeoScatterPoint, tokens: EChartsThemeTokens, labelScale: number) {
  const customLabelScale = point.label?.scale ?? 1.0;
  const fontSize = Math.max(
    MIN_POINT_LABEL_FONT_SIZE,
    BASE_POINT_LABEL_FONT_SIZE * labelScale * customLabelScale
  );
  const effectiveScale = fontSize / BASE_POINT_LABEL_FONT_SIZE;

  return {
    id: point.id,
    name: point.name ?? point.id,
    value: point.position,
    tooltip: point.tooltip,
    symbol: point.symbol,
    symbolSize: point.symbolSize,
    itemStyle: {
      color: point.color,
      borderColor: point.borderColor,
      borderWidth: point.borderWidth,
    },
    label: point.label
      ? {
          show: true,
          position: point.label.position ?? "top",
          formatter: formatPointLabel(point),
          fontSize,
          fontWeight: "bold",
          fontFamily: tokens.fontFamily,
          color: tokens.textColor,
          distance: 4 * effectiveScale,
          backgroundColor: tokens.tooltipBackgroundColor,
          borderColor: tokens.tooltipBorderColor,
          borderWidth: 1,
          borderRadius: 3 * effectiveScale,
          padding: [2 * effectiveScale, 4 * effectiveScale] as [number, number],
          rich: {
            primary: { color: tokens.textColor },
            secondary: {
              color: point.label.secondaryColor ?? tokens.textColor,
              fontWeight: "bold",
            },
          },
        }
      : undefined,
  };
}

function formatTooltip(params: TooltipComponentFormatterCallbackParams): string {
  const candidate = Array.isArray(params) ? params[0] : params;
  const data = candidate?.data as { tooltip?: string } | undefined;
  return data?.tooltip ?? "";
}

/**
 * Builds a single custom series that draws every boundary as an ECharts
 * built-in `polygon` graphic shape, one per data index. Each vertex is
 * projected through `api.coord` so the shape stays aligned with the geo
 * layer's pan/zoom, the same mechanism the heatmap layer above uses for its
 * image bounds.
 */
export function calculatePolygonLabelPoint(points: [number, number][]): [number, number] | null {
  if (points.length === 0) return null;
  if (points.length < 3) {
    const totals = points.reduce(
      ([x, y], point) => [x + point[0], y + point[1]] as [number, number],
      [0, 0] as [number, number]
    );
    return [totals[0] / points.length, totals[1] / points.length];
  }

  let twiceArea = 0;
  let weightedX = 0;
  let weightedY = 0;
  for (let index = 0; index < points.length; index += 1) {
    const current = points.at(index)!;
    const next = points.at((index + 1) % points.length)!;
    const cross = current[0] * next[1] - next[0] * current[1];
    twiceArea += cross;
    weightedX += (current[0] + next[0]) * cross;
    weightedY += (current[1] + next[1]) * cross;
  }

  if (Math.abs(twiceArea) < Number.EPSILON) {
    const totals = points.reduce(
      ([x, y], point) => [x + point[0], y + point[1]] as [number, number],
      [0, 0] as [number, number]
    );
    return [totals[0] / points.length, totals[1] / points.length];
  }

  return [weightedX / (3 * twiceArea), weightedY / (3 * twiceArea)];
}

/**
 * Builds the "boundaries" custom series. `dashOffset` drives a "marching
 * ants" effect for boundaries whose style sets `animated: true`: their
 * outline is forced to a dotted dash and its offset is shifted continuously,
 * without ever touching `stroke`/`fill` colors.
 */
export function createBoundariesSeries(
  boundaries: GeoScatterBoundary[],
  defaultStyle: GeoScatterBoundaryStyle,
  dashOffset = 0,
  pulseOpacity = 1,
  overrides?: { id?: string; z?: number }
) {
  const resolvedDefault = { ...DEFAULT_BOUNDARY_STYLE, ...defaultStyle };

  const renderItem: CustomSeriesRenderItem = (params, api) => {
    const boundary = boundaries[params.dataIndex];
    if (boundary == null) return { type: "group", children: [] };

    const style = { ...resolvedDefault, ...boundary.style };
    const isAnimated = boundary.style?.animated === true;
    const isPulsing = boundary.style?.pulse === true;
    const isClosed = boundary.closed !== false;

    return {
      type: isClosed ? "polygon" : "polyline",
      shape: { points: boundary.points.map((point) => api.coord(point)) },
      style: {
        fill: isClosed ? style.fill : undefined,
        stroke: style.stroke,
        lineWidth: style.strokeWidth,
        lineDash: isAnimated ? "dotted" : style.lineDash,
        ...(isAnimated ? { lineDashOffset: -dashOffset } : {}),
        ...(isPulsing ? { opacity: pulseOpacity } : {}),
      },
      silent: true,
    };
  };

  return {
    id: overrides?.id ?? "boundaries",
    type: "custom" as const,
    coordinateSystem: "geo" as const,
    geoIndex: 0,
    // Boundaries remain below lines and markers, but above the heatmap so
    // configured operational areas stay visible while heatmap mode is enabled.
    // Background-layer boundaries (e.g. a whole-floor observation area tint)
    // override this to sit behind the heatmap and detection points instead.
    z: overrides?.z ?? 3,
    animation: false,
    // Boundary colors are static map context, not interactive controls. Keeping
    // the series silent prevents custom-series hover state from altering fills
    // or outlines.
    silent: true,
    emphasis: { disabled: true },
    tooltip: { show: false },
    renderItem,
    data: boundaries.map((boundary) => ({
      id: boundary.id,
      name: boundary.name ?? boundary.id,
      tooltip: boundary.tooltip,
      value: boundary.points[0] ?? [0, 0],
    })),
  };
}

interface BoundaryLabelPlacement {
  id: string;
  position: [number, number];
}

interface LabelRectangle {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

function rectanglesOverlap(first: LabelRectangle, second: LabelRectangle): boolean {
  return !(
    first.right < second.left ||
    first.left > second.right ||
    first.bottom < second.top ||
    first.top > second.bottom
  );
}

/** Label box size for a boundary chip's text, sized to its longest line so multi-line KPI chips get an accurate box. */
function boundaryLabelSize(text: string): { width: number; height: number } {
  const longestLine = Math.max(...text.split("\n").map((line) => line.length));
  return {
    width: Math.max(42, longestLine * 7 + 14),
    height: text.includes("\n") ? 40 : 24,
  };
}

function boundaryLabelRectangle(position: [number, number], text: string): LabelRectangle {
  const { width, height } = boundaryLabelSize(text);
  return {
    left: position[0] - width / 2,
    right: position[0] + width / 2,
    top: position[1] - height / 2,
    bottom: position[1] + height / 2,
  };
}

/** Places labels near polygon centroids while avoiding earlier label text boxes. */
export function calculateNonOverlappingBoundaryLabelPositions(
  boundaries: GeoScatterBoundary[],
  toPixel: (point: [number, number]) => [number, number]
): BoundaryLabelPlacement[] {
  const placements: BoundaryLabelPlacement[] = [];
  const occupied: LabelRectangle[] = [];
  const labelledBoundaries = boundaries.filter((boundary) => boundary.label != null);

  for (const boundary of labelledBoundaries) {
    if (!boundary.label) continue;
    const labelPoint = calculatePolygonLabelPoint(boundary.points);
    if (!labelPoint) continue;
    const anchor = toPixel(labelPoint);
    const { width, height } = boundaryLabelSize(boundary.label.text);
    const offsets: [number, number][] = [[0, 0]];
    for (let step = 1; step <= labelledBoundaries.length + 4; step += 1) {
      const verticalOffset = step * (height + 4);
      offsets.push([0, -verticalOffset], [0, verticalOffset]);
      if (step > 2) {
        offsets.push([-width * 0.65, -verticalOffset], [width * 0.65, verticalOffset]);
      }
    }

    let selectedPosition = anchor;
    let selectedRectangle: LabelRectangle | null = null;
    for (const [offsetX, offsetY] of offsets) {
      const position: [number, number] = [anchor[0] + offsetX, anchor[1] + offsetY];
      const rectangle = {
        left: position[0] - width / 2 - 3,
        right: position[0] + width / 2 + 3,
        top: position[1] - height / 2 - 3,
        bottom: position[1] + height / 2 + 3,
      };
      if (occupied.every((existing) => !rectanglesOverlap(existing, rectangle))) {
        selectedPosition = position;
        selectedRectangle = rectangle;
        break;
      }
    }

    const rectangle = selectedRectangle ?? {
      left: selectedPosition[0] - width / 2,
      right: selectedPosition[0] + width / 2,
      top: selectedPosition[1] - height / 2,
      bottom: selectedPosition[1] + height / 2,
    };
    occupied.push(rectangle);
    placements.push({ id: boundary.id, position: selectedPosition });
  }

  return placements;
}

function createBoundaryLabelsSeries(boundaries: GeoScatterBoundary[]) {
  const labelledBoundaries = boundaries.filter((boundary) => boundary.label != null);
  const renderItem: CustomSeriesRenderItem = (params, api) => {
    const boundary = labelledBoundaries.at(params.dataIndex);
    if (!boundary?.label) return { type: "group", children: [] };
    const placements = calculateNonOverlappingBoundaryLabelPositions(
      labelledBoundaries,
      (point) => {
        const pixel = api.coord(point);
        return [pixel[0], pixel[1]];
      }
    );
    const placement = placements.find((candidate) => candidate.id === boundary.id);
    if (!placement) return { type: "group", children: [] };
    const position = placement.position;

    return {
      type: "text",
      x: position[0],
      y: position[1],
      silent: true,
      style: {
        text: boundary.label.text,
        fill: boundary.label.color,
        backgroundColor: boundary.label.backgroundColor,
        padding: [4, 7] as [number, number],
        borderRadius: 3,
        align: "center" as const,
        verticalAlign: "middle" as const,
        font: "600 12px sans-serif",
        lineHeight: 18,
      },
    };
  };

  return {
    id: "boundary-labels",
    type: "custom" as const,
    coordinateSystem: "geo" as const,
    geoIndex: 0,
    silent: true,
    animation: false,
    tooltip: { show: false },
    // Above sensor markers and gate labels: a boundary's own KPI chip is the
    // top-most annotation for that boundary.
    z: 9,
    renderItem,
    data: labelledBoundaries.map((boundary) => ({
      id: `${boundary.id}-label`,
      value: calculatePolygonLabelPoint(boundary.points) ?? [0, 0],
    })),
  };
}

const DIRECTIONAL_TRIANGLE_SIZE = 8;
const DIRECTIONAL_TRIANGLE_SPACING = 2;
const DIRECTIONAL_MARKER_POSITIONS = [0.25, 0.5, 0.75];

function createDirectionalTriangle(
  center: [number, number],
  direction: [number, number],
  size = DIRECTIONAL_TRIANGLE_SIZE
): [number, number][] {
  const perpendicular: [number, number] = [-direction[1], direction[0]];
  const tip: [number, number] = [center[0] + direction[0] * size, center[1] + direction[1] * size];
  const baseCenter: [number, number] = [
    center[0] - direction[0] * size,
    center[1] - direction[1] * size,
  ];
  const halfBase = size * 0.65;

  return [
    tip,
    [baseCenter[0] + perpendicular[0] * halfBase, baseCenter[1] + perpendicular[1] * halfBase],
    [baseCenter[0] - perpendicular[0] * halfBase, baseCenter[1] - perpendicular[1] * halfBase],
  ];
}

type CustomGroupChildren = Extract<
  NonNullable<CustomSeriesRenderItemReturn>,
  { type: "group" }
>["children"];

/** Screen-length spacing target for repeated gate direction markers (see computeGateMarkerPositions). */
const GATE_MARKER_SPACING_PX = 150;
const GATE_STEM_GAP = 2;
const GATE_STEM_LENGTH = 12;

interface GateArrowGeometry {
  stemStart: [number, number];
  stemEnd: [number, number];
  headTip: [number, number];
  /** Total distance from the gate line to the arrowhead's tip. */
  reach: number;
}

/** A short stem plus filled arrowhead crossing the gate line, replacing the old outline chevron. */
function computeGateArrowGeometry(
  onLine: [number, number],
  arrowNormal: [number, number],
  headSize: number
): GateArrowGeometry {
  const stemStart: [number, number] = [
    onLine[0] + arrowNormal[0] * GATE_STEM_GAP,
    onLine[1] + arrowNormal[1] * GATE_STEM_GAP,
  ];
  const headBaseDistance = GATE_STEM_GAP + GATE_STEM_LENGTH;
  const stemEnd: [number, number] = [
    onLine[0] + arrowNormal[0] * headBaseDistance,
    onLine[1] + arrowNormal[1] * headBaseDistance,
  ];
  const reach = headBaseDistance + headSize;
  const headTip: [number, number] = [
    onLine[0] + arrowNormal[0] * reach,
    onLine[1] + arrowNormal[1] * reach,
  ];
  return { stemStart, stemEnd, headTip, reach };
}

/**
 * Screen-length-based marker count for a gate line: one marker per ~150px of
 * rendered length, clamped to 1-3, symmetric around the midpoint so a single
 * IN/OUT label anchored at 0.5 always lines up with the middle (or only) marker.
 */
function computeGateMarkerPositions(lineLengthPx: number): number[] {
  const count = Math.max(1, Math.min(3, Math.round(lineLengthPx / GATE_MARKER_SPACING_PX)));
  if (count === 1) return [0.5];
  if (count === 2) return [0.32, 0.68];
  return DIRECTIONAL_MARKER_POSITIONS;
}

function gateLabelRectangle(center: [number, number], label: string): LabelRectangle {
  const halfWidth = Math.max(28, label.length * 3.5 + 8);
  const halfHeight = 12;
  return {
    left: center[0] - halfWidth,
    right: center[0] + halfWidth,
    top: center[1] - halfHeight,
    bottom: center[1] + halfHeight,
  };
}

interface GateLineGeometry {
  start: [number, number];
  lineVector: [number, number];
  arrowNormal: [number, number];
  preferredNormal: [number, number];
}

function computeGateLineGeometry(
  line: GeoDirectionalLine,
  api: CustomSeriesRenderItemAPI
): GateLineGeometry | null {
  const start = api.coord(line.start) as [number, number];
  const end = api.coord(line.end);
  const reference = api.coord(line.positiveReferencePoint);
  const lineVector: [number, number] = [end[0] - start[0], end[1] - start[1]];
  const lineLength = Math.hypot(lineVector[0], lineVector[1]);
  if (lineLength === 0) return null;

  const unitAlongLine: [number, number] = [lineVector[0] / lineLength, lineVector[1] / lineLength];
  const leftNormal: [number, number] = [-unitAlongLine[1], unitAlongLine[0]];
  const referenceVector: [number, number] = [reference[0] - start[0], reference[1] - start[1]];
  const referenceSide = referenceVector[0] * leftNormal[0] + referenceVector[1] * leftNormal[1];
  const positiveNormal: [number, number] =
    referenceSide >= 0 ? leftNormal : [-leftNormal[0], -leftNormal[1]];
  const arrowNormal: [number, number] =
    line.arrowSide === "negative" ? [-positiveNormal[0], -positiveNormal[1]] : positiveNormal;

  return {
    start,
    lineVector,
    arrowNormal,
    preferredNormal: [-arrowNormal[0], -arrowNormal[1]],
  };
}

/**
 * Places every single-sided gate line's (queue entrance/exit) IN/OUT label as
 * one visual unit with its arrow: anchored at the gate's midpoint, stacked
 * just beyond the arrowhead on the arrow's own side. Falls back to the
 * opposite side of the line at the same midpoint, then a small slide along
 * the line, and finally hides the label rather than displacing it far from
 * its gate. Earlier lines in `directionalLines` (the selected queue's gates,
 * by caller convention) claim space first.
 */
function calculateGateLabelPlacements(
  directionalLines: GeoDirectionalLine[],
  api: CustomSeriesRenderItemAPI,
  chipRectangles: LabelRectangle[]
): Map<string, [number, number] | null> {
  const placements = new Map<string, [number, number] | null>();
  const claimed = [...chipRectangles];

  for (const line of directionalLines) {
    // Only single-sided gate lines opt into shared, collision-aware placement.
    if (line.arrowSide === undefined) continue;

    const geometry = computeGateLineGeometry(line, api);
    if (!geometry) {
      placements.set(line.id, null);
      continue;
    }

    const { start, lineVector, arrowNormal, preferredNormal } = geometry;
    const arrowSize = line.arrowSize ?? DIRECTIONAL_TRIANGLE_SIZE;
    const midpoint: [number, number] = [
      start[0] + lineVector[0] * 0.5,
      start[1] + lineVector[1] * 0.5,
    ];
    const { reach } = computeGateArrowGeometry(midpoint, arrowNormal, arrowSize);

    // Reserve the arrowhead itself so a label never lands on top of it.
    const arrowPadding = arrowSize + 4;
    const arrowCenter: [number, number] = [
      midpoint[0] + arrowNormal[0] * reach,
      midpoint[1] + arrowNormal[1] * reach,
    ];
    claimed.push({
      left: arrowCenter[0] - arrowPadding,
      right: arrowCenter[0] + arrowPadding,
      top: arrowCenter[1] - arrowPadding,
      bottom: arrowCenter[1] + arrowPadding,
    });

    const labelClearance = 10;
    const onArrowSideOffset = reach + labelClearance;
    const onOppositeSideOffset = arrowSize + labelClearance;

    const candidates: { anchor: [number, number]; side: [number, number]; offset: number }[] = [
      { anchor: midpoint, side: arrowNormal, offset: onArrowSideOffset },
      { anchor: midpoint, side: preferredNormal, offset: onOppositeSideOffset },
      ...[0.35, 0.65].map((t) => ({
        anchor: [start[0] + lineVector[0] * t, start[1] + lineVector[1] * t] as [number, number],
        side: arrowNormal,
        offset: onArrowSideOffset,
      })),
    ];

    let placed: [number, number] | null = null;
    for (const { anchor, side, offset } of candidates) {
      const candidate: [number, number] = [
        anchor[0] + side[0] * offset,
        anchor[1] + side[1] * offset,
      ];
      const rectangle = gateLabelRectangle(candidate, line.label);
      if (claimed.every((existing) => !rectanglesOverlap(existing, rectangle))) {
        placed = candidate;
        claimed.push(rectangle);
        break;
      }
    }

    placements.set(line.id, placed);
  }

  return placements;
}

function createDirectionalLineChildren(
  line: GeoDirectionalLine,
  api: CustomSeriesRenderItemAPI
): CustomGroupChildren {
  const start = api.coord(line.start);
  const end = api.coord(line.end);
  const reference = api.coord(line.positiveReferencePoint);
  const lineVector: [number, number] = [end[0] - start[0], end[1] - start[1]];
  const lineLength = Math.hypot(lineVector[0], lineVector[1]);
  const opacity = line.emphasis?.opacity ?? line.opacity ?? 1;
  const width = line.emphasis?.width ?? line.width;

  if (lineLength === 0) return [];

  const unitAlongLine: [number, number] = [lineVector[0] / lineLength, lineVector[1] / lineLength];
  const leftNormal: [number, number] = [-unitAlongLine[1], unitAlongLine[0]];
  const referenceVector: [number, number] = [reference[0] - start[0], reference[1] - start[1]];
  const referenceSide = referenceVector[0] * leftNormal[0] + referenceVector[1] * leftNormal[1];
  const positiveNormal: [number, number] =
    referenceSide >= 0 ? leftNormal : [-leftNormal[0], -leftNormal[1]];
  const negativeNormal: [number, number] = [-positiveNormal[0], -positiveNormal[1]];
  const arrowSize = line.arrowSize ?? DIRECTIONAL_TRIANGLE_SIZE;
  const markerOffset = arrowSize + DIRECTIONAL_TRIANGLE_SPACING;
  const isGateLine = line.arrowSide !== undefined;
  const markerPositions = isGateLine
    ? computeGateMarkerPositions(lineLength)
    : DIRECTIONAL_MARKER_POSITIONS;

  const children: CustomGroupChildren = [];

  // Gate lines get a subtle dark halo under their neutral line so they stay
  // visible over the heatmap without needing a saturated, semantic color.
  if (isGateLine) {
    children.push({
      type: "line" as const,
      shape: { x1: start[0], y1: start[1], x2: end[0], y2: end[1] },
      style: { stroke: "rgba(0, 0, 0, 0.45)", lineWidth: width + 2, opacity },
      silent: true,
    });
  }
  children.push({
    type: "line" as const,
    shape: { x1: start[0], y1: start[1], x2: end[0], y2: end[1] },
    style: { stroke: line.color, lineWidth: width, opacity },
    silent: true,
  });

  for (const position of markerPositions) {
    const onLine: [number, number] = [
      start[0] + lineVector[0] * position,
      start[1] + lineVector[1] * position,
    ];

    if (isGateLine) {
      // A short stem plus filled arrowhead crossing the gate, read as "people
      // crossing this line in this direction" rather than a stand-alone map
      // symbol; repeated along long gates while keeping a single label.
      const arrowNormal = line.arrowSide === "negative" ? negativeNormal : positiveNormal;
      const { stemStart, stemEnd } = computeGateArrowGeometry(onLine, arrowNormal, arrowSize);
      children.push({
        type: "line" as const,
        shape: { x1: stemStart[0], y1: stemStart[1], x2: stemEnd[0], y2: stemEnd[1] },
        style: { stroke: line.color, lineWidth: 2, opacity },
        silent: true,
      });
      children.push({
        type: "polygon" as const,
        shape: { points: createDirectionalTriangle(stemEnd, arrowNormal, arrowSize) },
        style: { fill: line.color, opacity },
        silent: true,
      });
      continue;
    }

    const positiveCenter: [number, number] = [
      onLine[0] + positiveNormal[0] * markerOffset,
      onLine[1] + positiveNormal[1] * markerOffset,
    ];
    const negativeCenter: [number, number] = [
      onLine[0] + negativeNormal[0] * markerOffset,
      onLine[1] + negativeNormal[1] * markerOffset,
    ];
    const createArrow = (center: [number, number], direction: [number, number], color: string) => ({
      type: "polygon" as const,
      shape: { points: createDirectionalTriangle(center, direction, arrowSize) },
      style: { fill: color, stroke: "#000000", lineWidth: 1.5, opacity },
      silent: true,
    });

    if (line.arrowSide !== "negative") {
      children.push(createArrow(positiveCenter, positiveNormal, line.positiveColor));
    }
    if (line.arrowSide !== "positive") {
      children.push(createArrow(negativeCenter, negativeNormal, line.negativeColor));
    }
  }

  return children;
}

/**
 * Builds the map-aligned custom series used for gate/throughput lines and
 * their direction markers only (no labels — see createDirectionalLineLabelsSeries,
 * kept in a separate, higher-`z` series so labels always paint above sensor
 * markers and detection points).
 */
export function createDirectionalLinesSeries(directionalLines: GeoDirectionalLine[]) {
  const renderItem: CustomSeriesRenderItem = (params, api) => {
    const line = directionalLines[params.dataIndex];
    if (!line) return { type: "group", children: [] };

    return {
      type: "group",
      children: createDirectionalLineChildren(line, api),
      silent: true,
    };
  };

  return {
    id: "directional-lines",
    type: "custom" as const,
    coordinateSystem: "geo" as const,
    geoIndex: 0,
    silent: true,
    animation: false,
    tooltip: { show: false },
    emphasis: { disabled: true },
    z: 5,
    renderItem,
    data: directionalLines.map((line) => ({ id: line.id, value: line.start })),
  };
}

/**
 * Builds the labels for directional lines. `chipBoundaries` are the labelled
 * boundaries (e.g. queue KPI chips) whose final on-screen positions gate-line
 * labels must avoid; pass the same array used to build the "boundaries"
 * series so both stay in sync. Earlier entries in `directionalLines` claim
 * label space first (callers order the selected queue's gates first for
 * priority).
 */
export function createDirectionalLineLabelsSeries(
  directionalLines: GeoDirectionalLine[],
  chipBoundaries: GeoScatterBoundary[] = []
) {
  const renderItem: CustomSeriesRenderItem = (params, api) => {
    const line = directionalLines[params.dataIndex];
    if (!line) return { type: "group", children: [] };

    const opacity = line.emphasis?.opacity ?? line.opacity ?? 1;
    const labelColor = line.emphasis?.labelColor ?? line.labelColor ?? line.color;
    const labelBackgroundColor =
      line.emphasis?.labelBackgroundColor ??
      line.labelBackgroundColor ??
      "rgba(255, 255, 255, 0.86)";
    const labelBorderColor =
      line.emphasis?.labelBorderColor ?? line.labelBorderColor ?? "rgba(0, 0, 0, 0.45)";

    let labelPosition: [number, number] | null;
    if (line.arrowSide !== undefined) {
      // Recomputed on every call, same as the boundary-label placement above;
      // cheap at the small number of queues/gates a deployment renders.
      const chipPlacements = calculateNonOverlappingBoundaryLabelPositions(
        chipBoundaries,
        (point): [number, number] => {
          const pixel = api.coord(point);
          return [pixel[0], pixel[1]];
        }
      );
      const chipRectangles = chipPlacements.flatMap((placement) => {
        const boundary = chipBoundaries.find((candidate) => candidate.id === placement.id);
        return boundary?.label
          ? [boundaryLabelRectangle(placement.position, boundary.label.text)]
          : [];
      });
      const gatePlacements = calculateGateLabelPlacements(directionalLines, api, chipRectangles);
      labelPosition = gatePlacements.get(line.id) ?? null;
    } else {
      const start = api.coord(line.start) as [number, number];
      const end = api.coord(line.end);
      const reference = api.coord(line.positiveReferencePoint);
      const lineVector: [number, number] = [end[0] - start[0], end[1] - start[1]];
      const lineLength = Math.hypot(lineVector[0], lineVector[1]);
      if (lineLength === 0) {
        labelPosition = start;
      } else {
        const unitAlongLine: [number, number] = [
          lineVector[0] / lineLength,
          lineVector[1] / lineLength,
        ];
        const leftNormal: [number, number] = [-unitAlongLine[1], unitAlongLine[0]];
        const referenceVector: [number, number] = [
          reference[0] - start[0],
          reference[1] - start[1],
        ];
        const referenceSide =
          referenceVector[0] * leftNormal[0] + referenceVector[1] * leftNormal[1];
        const positiveNormal: [number, number] =
          referenceSide >= 0 ? leftNormal : [-leftNormal[0], -leftNormal[1]];
        const negativeNormal: [number, number] = [-positiveNormal[0], -positiveNormal[1]];
        const arrowSize = line.arrowSize ?? DIRECTIONAL_TRIANGLE_SIZE;
        const markerOffset = arrowSize + DIRECTIONAL_TRIANGLE_SPACING;
        // Keep the label near the start of the line, before the first
        // directional arrow at 25%, so it never sits on top of an arrow marker.
        const labelPositionAlongLine = 0.08;
        const labelAnchor: [number, number] = [
          start[0] + lineVector[0] * labelPositionAlongLine,
          start[1] + lineVector[1] * labelPositionAlongLine,
        ];
        const labelOffset = markerOffset + 10;
        const lowerLabelPosition: [number, number] = [
          labelAnchor[0] + negativeNormal[0] * labelOffset,
          labelAnchor[1] + negativeNormal[1] * labelOffset,
        ];
        const upperLabelPosition: [number, number] = [
          labelAnchor[0] + positiveNormal[0] * labelOffset,
          labelAnchor[1] + positiveNormal[1] * labelOffset,
        ];
        const labelHalfWidth = Math.max(28, line.label.length * 3.5 + 8);
        const labelHalfHeight = 12;
        const fitsInChart = ([x, y]: [number, number]) =>
          x - labelHalfWidth >= 0 &&
          x + labelHalfWidth <= api.getWidth() &&
          y - labelHalfHeight >= 0 &&
          y + labelHalfHeight <= api.getHeight();
        labelPosition = fitsInChart(lowerLabelPosition) ? lowerLabelPosition : upperLabelPosition;
      }
    }

    if (!labelPosition) return { type: "group", children: [] };

    return {
      type: "group",
      children: [
        {
          type: "text" as const,
          x: labelPosition[0],
          y: labelPosition[1],
          silent: true,
          style: {
            text: line.label,
            fill: labelColor,
            backgroundColor: labelBackgroundColor,
            borderColor: labelBorderColor,
            borderWidth: 1,
            borderRadius: 2,
            padding: [2, 4] as [number, number],
            font: "600 12px sans-serif",
            align: "center" as const,
            verticalAlign: "middle" as const,
            opacity,
          },
        },
      ],
      silent: true,
    };
  };

  return {
    id: "directional-line-labels",
    type: "custom" as const,
    coordinateSystem: "geo" as const,
    geoIndex: 0,
    silent: true,
    animation: false,
    tooltip: { show: false },
    emphasis: { disabled: true },
    z: 8,
    renderItem,
    data: directionalLines.map((line) => ({ id: line.id, value: line.start })),
  };
}

function createLineSegmentsSeries(lineSegments: GeoLineSegment[]) {
  const renderItem: CustomSeriesRenderItem = (params, api) => {
    const segment = lineSegments[params.dataIndex];
    if (!segment) return { type: "group", children: [] };

    const start = api.coord(segment.start);
    const end = api.coord(segment.end);
    return {
      type: "line",
      shape: { x1: start[0], y1: start[1], x2: end[0], y2: end[1] },
      style: { stroke: segment.color, lineWidth: segment.width },
      silent: true,
    };
  };

  return {
    id: "line-segments",
    type: "custom" as const,
    coordinateSystem: "geo" as const,
    geoIndex: 0,
    silent: true,
    animation: false,
    tooltip: { show: false },
    z: 4,
    renderItem,
    data: lineSegments.map((segment) => ({ id: segment.id, value: segment.start })),
  };
}

function createFootprintPointsSeries(points: GeoScatterPoint[]) {
  const renderItem: CustomSeriesRenderItem = (params, api) => {
    const point = points[params.dataIndex];
    if (point?.footprint == null) return { type: "group", children: [] };

    return {
      type: "polygon",
      shape: { points: point.footprint.map((vertex) => api.coord(vertex)) },
      style: {
        fill: point.color,
        stroke: point.borderColor,
        lineWidth: point.borderWidth,
      },
      silent: true,
    };
  };

  return {
    id: "points",
    type: "custom" as const,
    coordinateSystem: "geo" as const,
    geoIndex: 0,
    silent: true,
    animation: false,
    tooltip: { show: false },
    // Detection points render just above the heatmap and below all queue/AOI
    // boundary and gate annotations.
    z: 2,
    renderItem,
    data: points.map((point) => ({ id: point.id, value: point.position })),
  };
}

/**
 * Builds the "aoi-insertions" series: small clickable "+" markers placed at
 * the midpoint of an editable boundary's edges, letting the user insert a
 * new vertex at that position (e.g. splitting an AOI edge while editing it).
 */
function createInsertionPointsSeries(insertionPoints: GeoScatterPoint[]) {
  return {
    id: "aoi-insertions",
    type: "scatter" as const,
    coordinateSystem: "geo" as const,
    geoIndex: 0,
    symbol: "circle" as const,
    animation: false,
    tooltip: { show: false },
    // Highest annotation layer: hover/selection UI (AOI edit affordances) sit
    // above everything else, including queue KPI chips.
    z: 10,
    data: insertionPoints.map((point) => ({
      id: point.id,
      name: point.name ?? point.id,
      value: point.position,
      symbolSize: point.symbolSize ?? 18,
      itemStyle: {
        color: point.color,
        borderColor: point.borderColor,
        borderWidth: point.borderWidth,
      },
      label: {
        show: true,
        position: "inside" as const,
        formatter: "+",
        color: "#ffffff",
        fontWeight: "bold" as const,
        fontSize: 12,
      },
    })),
  };
}

/**
 * `min: 1` keeps the base fit-to-frame scale (image edge-to-edge top-to-bottom
 * or left-to-right) as the closest zoom-out; `max: 2` caps zoom-in at 2x.
 */
export const DEFAULT_ZOOM_LIMIT = { min: 1, max: 2 };

/**
 * Builds the ECharts option for a roam-able (zoom/pan) geo layer rendering a
 * registered SVG/image map, selectable scatter markers, and an optional soft
 * raster heatmap. One map-aligned image keeps live heat updates inexpensive.
 */
export function createGeoScatterChartOption(
  markers: GeoScatterMarker[],
  points: GeoScatterPoint[],
  heatmapLayer: GeoHeatmapLayer | null,
  boundaries: GeoScatterBoundary[],
  config: GeoScatterChartConfig,
  tokens: EChartsThemeTokens,
  lineSegments: GeoLineSegment[] = [],
  insertionPoints: GeoScatterPoint[] = [],
  directionalLines: GeoDirectionalLine[] = [],
  /**
   * The geo layers current roam scale (1 at its base fit-to-frame zoom, per
   * zoomLimit.min). Used, via computeLabelScale, to scale point labels
   * inversely to the maps own zoom, so they stay legible as the map is
   * zoomed in/out (see toPointDatum).
   */
  zoom = 1
): EChartsOption {
  const footprintPoints = points.filter((point) => point.footprint != null);
  const hasBoundaryLabels = boundaries.some((boundary) => boundary.label != null);
  const backgroundBoundaries = boundaries.filter((boundary) => boundary.layer === "background");
  const foregroundBoundaries = boundaries.filter((boundary) => boundary.layer !== "background");
  const silentPoints = points.filter((point) => point.tooltip == null && point.footprint == null);
  const interactivePoints = points.filter((point) => point.tooltip != null);
  const zoomLimit = config.zoomLimit ?? DEFAULT_ZOOM_LIMIT;
  const labelScale = computeLabelScale(zoom, zoomLimit);

  return {
    tooltip: {
      trigger: "item",
      confine: true,
      backgroundColor: tokens.tooltipBackgroundColor,
      borderColor: tokens.tooltipBorderColor,
      borderWidth: 1,
      padding: [8, 10],
      textStyle: {
        color: tokens.textColor,
        fontFamily: tokens.fontFamily,
        fontSize: 12,
      },
      formatter: formatTooltip,
    },
    geo: {
      map: config.mapName,
      roam: true,
      left: "center",
      top: "center",
      scaleLimit: zoomLimit,
    },
    series: [
      ...(heatmapLayer
        ? [
            {
              id: "heatmap",
              type: "custom" as const,
              coordinateSystem: "geo" as const,
              geoIndex: 0,
              silent: true,
              animation: false,
              tooltip: { show: false },
              // Strictly below detection points (z: 2) — don't rely on tie-breaking.
              z: 1,
              renderItem: createHeatmapRenderItem(heatmapLayer),
              data: [{ id: "heatmap-image", value: [heatmapLayer.x, heatmapLayer.y] }],
            },
          ]
        : []),
      ...(backgroundBoundaries.length > 0
        ? [
            createBoundariesSeries(backgroundBoundaries, config.boundaryStyle ?? {}, 0, 1, {
              id: "boundaries-background",
              z: 0,
            }),
          ]
        : []),
      ...(foregroundBoundaries.length > 0
        ? [createBoundariesSeries(foregroundBoundaries, config.boundaryStyle ?? {})]
        : []),
      ...(hasBoundaryLabels ? [createBoundaryLabelsSeries(boundaries)] : []),
      ...(lineSegments.length > 0 ? [createLineSegmentsSeries(lineSegments)] : []),
      ...(directionalLines.length > 0
        ? [
            createDirectionalLinesSeries(directionalLines),
            createDirectionalLineLabelsSeries(directionalLines, boundaries),
          ]
        : []),
      {
        id: "markers",
        type: "scatter",
        coordinateSystem: "geo",
        geoIndex: 0,
        symbolSize: 20,
        z: 6,
        data: markers.map(toMarkerDatum),
        emphasis: {
          scale: 1.2,
        },
      },
      createFootprintPointsSeries(footprintPoints),
      ...(silentPoints.length > 0
        ? [
            {
              id: "fixed-points",
              type: "scatter" as const,
              coordinateSystem: "geo" as const,
              geoIndex: 0,
              symbolSize: 10,
              silent: true,
              animation: false,
              tooltip: { show: false },
              z: 2,
              data: silentPoints.map((point) => toPointDatum(point, tokens, labelScale)),
            },
          ]
        : []),
      {
        id: "interactive-points",
        type: "scatter",
        coordinateSystem: "geo",
        geoIndex: 0,
        symbol: "rect",
        symbolSize: 14,
        animation: false,
        tooltip: { trigger: "item", triggerOn: "click" },
        z: 7,
        data: interactivePoints.map((point) => toPointDatum(point, tokens, labelScale)),
      },
      ...(insertionPoints.length > 0 ? [createInsertionPointsSeries(insertionPoints)] : []),
    ] as EChartsOption["series"],
  };
}
